import React from "react";
import { Link } from "react-router-dom";
import HeaderWithTag from "../common/header_with_tag";
import { HOST_API_URL } from "../config";
import { PATH_PRIVATE } from "../routes/paths";

const Home: React.FC = () => {
  return (
    <div className="app-content main-content mt-0">
      <div className="side-app">
        <div className="main-container container-fluid">
          <div className="page-header">
            <div>
              <h1 className="page-title">Get Started</h1>
            </div>
            <div className="ms-auto pageheader-btn">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="javascript:void(0);">Docs</a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Get Started
                </li>
              </ol>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12 ">
              <div className="card overflow-hidden">
                <div className="card-body p-0">
                  <div className="p-5" style={{ textAlign: "justify" }}>
                    <HeaderWithTag headerTag="overview" headerText="Overview" />
                    <p className="text-muted text-14">
                      The Dahabshiil Remittance API enables seamless integration for real-time money transfers and payouts. This documentation portal provides comprehensive endpoint references, code samples, and integration guides to help developers build robust remittance solutions. Start here to understand the API structure, then explore specific endpoints via the sidebar.
                    </p>

                    <div className="my-5">
                      <HeaderWithTag headerTag="quickStart" headerText="Quick Start" />
                      <ol style={{ marginLeft: 10 }} className="text-14">
                        <li>Generate an access token using the <strong>Get Token</strong> endpoint with your username and password.</li>
                        <li>Retrieve supported countries, agents, banks, and services using the respective GET endpoints.</li>
                        <li>Validate recipient accounts before sending funds via <strong>Validate Account</strong>.</li>
                        <li>Check exchange rates for accurate conversions.</li>
                        <li>Submit a remittance transaction using <strong>Send Remittance</strong>.</li>
                        <li>Query transaction status and details via <strong>Transaction Details</strong>.</li>
                        <li>Handle cancellations or updates as needed with <strong>Cancel Remittance</strong> or <strong>Update Beneficiary Details</strong>.</li>
                      </ol>
                    </div>

                    <div className="my-5">
                      <HeaderWithTag headerTag="auth" headerText="Authentication & Security" />
                      <p className="text-14">
                        Authentication is handled via Bearer tokens obtained from the <strong>Get Token</strong> endpoint. Include the token in the <code>Authorization</code> header for all API requests. Tokens are valid for 10 minutes; the exact expiry is returned in <code>data.tokenExpiry</code> and <code>data.tokenValidity</code>, so read those rather than hard-coding the lifetime. A request made with a missing, invalid or expired token is answered with HTTP <strong>401</strong> and an empty body: request a new token and retry. Ensure requests originate from secure, authorized environments.
                      </p>
                      <div className="alert alert-info">
                        <strong>Tip:</strong> Store tokens securely and avoid exposing them in client-side code or logs.
                      </div>
                      <div >
                        <HeaderWithTag headerTag="headers" headerText="Headers" />
                        <p className="text-14">
                          For all requests to the Dahabshiil Remittance API, include the Bearer token in the <code>Authorization</code> header. This token is obtained via the Get Token endpoint and is required for authenticated access.
                        </p>
                        <pre className="language-js p-3 border border-1">
                          {`curl -X POST "${HOST_API_URL}/Remittances/ValidateAccount" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"account": "201018078601", "entity": "DBI", "type": "bank"}'`}
                        </pre>
                      </div>
                    </div>

                    <div className="my-5">
                      <HeaderWithTag headerTag="workflow" headerText="Typical Workflow" />
                      <ol style={{ marginLeft: 10 }} className="text-14">
                        <li>Authenticate: Obtain a Bearer token via <strong>Get Token</strong>.</li>
                        <li>Discover: Fetch available countries, agents, banks, and services.</li>
                        <li>Validate: Confirm account details using <strong>Validate Account</strong>.</li>
                        <li>Convert: Check exchange rates for accurate amounts.</li>
                        <li>Transfer: Initiate remittance with <strong>Send Remittance</strong>.</li>
                        <li>Monitor: Retrieve transaction details and status.</li>
                        <li>Manage: Cancel or update transactions as required.</li>
                      </ol>
                    </div>

                    <div className="my-5">
                      <HeaderWithTag headerTag="response" headerText="Response Envelope" />
                      <p className="text-14">
                        Every endpoint returns the same envelope. On success <code>message</code> is
                        <code>"Success"</code> and <code>errorCode</code> is <code>null</code>; the
                        endpoint's payload is always inside <code>data</code>.
                      </p>
                      <pre className="language-js p-3 border border-1">
                        {`{
  "statusCode": 200,
  "message": "Success",
  "errorCode": null,
  "data": { ... }
}`}
                      </pre>
                      <p className="text-14">
                        A failure uses the same shape, with the reason in <code>message</code> and a
                        stable code in <code>errorCode</code>:
                      </p>
                      <pre className="language-js p-3 border border-1">
                        {`{
  "statusCode": 400,
  "message": "Insufficient balance to process this remittance",
  "errorCode": "DPI-3001",
  "data": null
}`}
                      </pre>
                      <p className="text-14">
                        When several fields fail validation at once, <code>message</code> lists every
                        failure and <code>errorCode</code> reflects the first; on these
                        request-validation failures <code>data</code> is a fixed descriptive string
                        rather than <code>null</code>. Parse the envelope keys case-insensitively:
                        403 responses and unexpected-error 500 responses are emitted as
                        <code> StatusCode / Message / ErrorCode / Data</code>.
                      </p>
                    </div>

                    <div className="my-5">
                      <HeaderWithTag headerTag="errors" headerText="Error Handling" />
                      <p className="text-14">
                        Branch on <code>errorCode</code>, not on <code>message</code> &mdash; message
                        wording may change, codes will not. The first digit after <code>DPI-</code>
                        identifies the category and determines whether a retry is safe. See the{" "}
                        <Link to={PATH_PRIVATE.ErrorCodes}>Error Codes</Link> page for the full list
                        and the recommended action per category; every endpoint page links to it
                        from its Error Handling section.
                      </p>
                      <div className="alert alert-warning">
                        <strong>Two exceptions.</strong> <code>errorCode</code> is <code>null</code> on
                        <strong> 429 Rate limit exceeded</strong> responses and on the
                        <strong> Get Token</strong> 404 (user not found or inactive). Branch on the
                        HTTP status in those two cases. A <strong>401</strong> (missing, invalid or
                        expired token) has no body at all.
                      </div>
                    </div>

                    <div className="my-5">
                      <HeaderWithTag headerTag="idempotency" headerText="Idempotency" />
                      <p className="text-14">
                        <strong>Send Remittance</strong> is guarded by the <code>requestUniqueId</code>
                        you supply. It is held for a 10-minute sliding window; a second request carrying
                        the same value inside that window is rejected with <code>DPI-5001</code> instead
                        of creating a second payout. This is what makes a network timeout safe to retry.
                      </p>
                      <p className="text-14">
                        If a request is rejected by validation before the transaction is created, the
                        <code> requestUniqueId</code> is released &mdash; so you may resubmit the corrected
                        request under the <strong>same</strong> value. A request that reached
                        transaction creation, or that failed with an unexpected error
                        (<code>DPI-9001</code>) after the duplicate check, keeps its id for the
                        window. Every rejected duplicate restarts the 10-minute window, so a tight
                        retry loop never clears. The <code>DPI-5001</code> response does not return
                        the original <code>transactionId</code>.
                      </p>
                      <div className="alert alert-info mb-0">
                        <code>requestUniqueId</code> guards the <em>request</em>;
                        <code> origRefNumber</code> is your own end-to-end reference stored against the
                        <em> transaction</em> for reconciliation. Both should be unique per transaction;
                        use a value such as a UUID, as the check is on the exact string. If the
                        outcome of a request is genuinely unknown (timeout, <code>DPI-9xxx</code>),
                        query <strong>Get Transaction Details</strong> passing your
                        <code> origRefNumber</code> as <code>ttno</code> before resubmitting.
                      </div>
                    </div>

                    <div className="my-5">
                      <HeaderWithTag headerTag="limits" headerText="Rate Limits & Request Tracing" />
                      <p className="text-14">
                        Requests are limited to <strong>1000 per sliding 60-second window</strong> per
                        authenticated user (per IP address for Get Token). Exceeding it returns HTTP
                        <strong> 429</strong> with a <code>Retry-After: 60</code> header and the message
                        "Rate limit exceeded. Try again later."; <code>errorCode</code> is null, so
                        detect it by status. Requests rejected with 429 still count toward the window,
                        so back off for the full <code>Retry-After</code> period rather than polling.
                      </p>
                      <p className="text-14 mb-0">
                        Every response that reaches the API proper carries an <code>X-Request-Id</code>{" "}
                        header. Capture and log it &mdash; it is the same identifier quoted inside
                        <code> DPI-3005</code> and <code>DPI-9001</code> messages, and it is what our
                        support team uses to locate your request. It is absent on 401 and 429
                        responses, which are answered before the request is processed.
                      </p>
                    </div>

                    <div className="my-5">
                      <HeaderWithTag headerTag="next" headerText="Where to Go Next" />
                      <ol style={{ marginLeft: 10 }} className="text-14">
                        <li>Start with <strong>Get Token</strong> to authenticate.</li>
                        <li>Explore reference data: <strong>Get Countries</strong>, <strong>Get Agents</strong>, etc.</li>
                        <li>Validate accounts and check rates before sending remittances.</li>
                        <li>Review code samples in each endpoint's Try panel.</li>
                        <li>Refer to the Appendix for enums and additional details.</li>
                        <li>Monitor API responses for errors and status updates.</li>
                      </ol>
                    </div>

                    <div className="my-5">
                      <HeaderWithTag headerTag="support" headerText="Support & Feedback" />
                      <p className="text-14 mb-2">
                        Encounter issues or need clarification? Contact your Dahabshiil integration manager with details like endpoint, request payload, response status, and any error messages. For technical support, include timestamps and transaction IDs.
                      </p>
                      <p className="text-14">
                        We're committed to improving the API based on user feedback—share your experiences to help us enhance the platform.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
